/**
 * Permission catalog.
 *
 * Permissions are *capabilities of the code*: they are declared here and synced to the
 * `permissions` table by migrations/seed. Roles and the role → permission mapping are
 * *data* (administrable per tenant). A permission granted to a role always carries a scope:
 *
 *  - `own`       : only records the user is linked to (owner / assignee)
 *  - `territory` : records of the territories the user is attached to
 *  - `all`       : every record of the tenant
 *
 * Non-scoped permissions (administration) are always stored with scope `all`.
 */

export const SCOPES = ['own', 'territory', 'all'] as const;
export type Scope = (typeof SCOPES)[number];

export const SCOPE_RANK: Record<Scope, number> = { own: 1, territory: 2, all: 3 };

export function maxScope(a: Scope | undefined, b: Scope): Scope {
  if (!a) return b;
  return SCOPE_RANK[a] >= SCOPE_RANK[b] ? a : b;
}

export interface PermissionDef {
  code: string;
  module: string;
  label: string;
  /** true when the permission applies to records and honours own/territory/all */
  scoped: boolean;
  /** `active` = enforced by current code; `planned` = declared for later modules */
  status: 'active' | 'planned';
}

const p = (
  code: string,
  module: string,
  label: string,
  scoped: boolean,
  status: 'active' | 'planned' = 'active',
): PermissionDef => ({ code, module, label, scoped, status });

export const PERMISSIONS = [
  // Administration
  p('tenant.settings.manage', 'administration', 'Gérer les paramètres de l’entreprise', false),
  p('legal_text.manage', 'administration', 'Publier les textes juridiques (conditions, consentements, réserves)', false),
  p('territory.manage', 'administration', 'Gérer les territoires', false),
  p('user.read', 'administration', 'Voir l’équipe', false),
  p('user.manage', 'administration', 'Créer / modifier / désactiver les comptes', false),
  p('user.assign_roles', 'administration', 'Attribuer rôles, territoires et droits individuels', false),
  p('role.read', 'administration', 'Voir les rôles et permissions', false),
  p('role.manage', 'administration', 'Gérer les rôles et permissions', false),
  p('audit.read', 'administration', 'Consulter le journal d’audit', true),
  p('privacy.manage', 'administration', 'Données personnelles : opposition aux sollicitations, export des données d’un client', false),
  p('referential.read', 'referentiels', 'Lire les référentiels', false),
  p('referential.manage', 'referentiels', 'Administrer les référentiels (types, motifs, listes)', false),

  // Clients
  p('client.read', 'clients', 'Voir les clients', true),
  p('client.create', 'clients', 'Créer un client', true),
  p('client.update', 'clients', 'Modifier un client', true),
  p('client.delete', 'clients', 'Supprimer (archiver) un client', true),

  // Prospects
  p('prospect.read', 'prospects', 'Voir les prospects (les siens selon la portée)', true),
  p('prospect.manage', 'prospects', 'Saisir / importer / attribuer les prospects', true),

  // Dossiers
  p('dossier.read', 'dossiers', 'Voir les dossiers', true),
  p('dossier.create', 'dossiers', 'Créer un dossier', true),
  p('dossier.update', 'dossiers', 'Compléter / modifier un dossier (brouillon ou à corriger)', true),
  p('dossier.submit', 'dossiers', 'Soumettre un dossier au contrôle', true),
  p('dossier.control', 'dossiers', 'Contrôler : renvoyer « À corriger » ou valider', true),
  p('dossier.schedule', 'dossiers', 'Passer un dossier validé « À planifier »', true),
  p('dossier.cancel', 'dossiers', 'Annuler un dossier', true),
  p('dossier.delete', 'dossiers', 'Supprimer un brouillon', true),
  p('dossier.assign', 'dossiers', 'Changer le commercial d’un dossier', true),
  p('dossier.line.substitute', 'dossiers', 'Changer le modèle d’un appareil, même après signature (prix inchangé, tracé)', true),
  p('dossier.delete_any', 'dossiers', 'Supprimer un dossier quel que soit son statut (motif obligatoire, tracé)', true),
  p('dossier.price.override', 'dossiers', 'Modifier le prix d’un produit pour un dossier (et l’ancien prix barré)', true),
  p('dossier.amounts.read', 'dossiers', 'Voir les montants des dossiers (prix, remises, totaux, bon de commande chiffré)', true),
  p('comment.private.read', 'dossiers', 'Lire les commentaires privés administrateur', true),
  p('comment.private.write', 'dossiers', 'Écrire des commentaires privés administrateur', true),

  // Modules des phases suivantes (déclarés pour que la matrice soit complète dès la V1)
  p('catalog.read', 'catalogue', 'Voir le catalogue et les prix', false),
  p('catalog.manage', 'catalogue', 'Gérer le catalogue : sections, familles, marques, articles, options et prix', false),
  p('commission.rules.manage', 'catalogue', 'Définir les barèmes de commission et de rémunération', false),
  p('document.read', 'documents', 'Consulter documents et photos', true),
  p('document.upload', 'documents', 'Ajouter documents et photos (et supprimer les siens)', true),
  p('signature.collect', 'documents', 'Faire signer le client', true),
  p('signature.manage', 'dossiers', 'Modifier, annuler ou supprimer une signature (quel que soit le statut du dossier)', true),
  p('portal.link.manage', 'documents', 'Générer / révoquer un lien portail client', true),
  p('planning.read', 'planning', 'Voir le planning (détaillé selon portée, anonymisé sinon)', true),
  p('planning.manage', 'planning', 'Planifier, attribuer et réattribuer', true),
  p('intervention.read', 'interventions', 'Voir les interventions', true),
  p('intervention.execute', 'interventions', 'Accepter / refuser / réaliser une intervention', true),
  p('intervention.validate_for_technician', 'interventions', 'Valider ou terminer une intervention à la place du technicien', true),
  p('payment.read', 'paiements', 'Voir les paiements', true),
  p('payment.collect', 'paiements', 'Encaisser (Stripe, espèces, chèque…)', true),
  p('payment.cash.confirm', 'paiements', 'Confirmer une remise d’espèces / un encaissement hors Stripe', true),
  p('payment.refund', 'paiements', 'Rembourser', true),
  p('payment.method_change', 'paiements', 'Changer le moyen de paiement d’un paiement non encaissé (tracé)', true),
  p('payment_method.manage', 'paiements', 'Gérer les moyens de paiement', false),
  p('invoice.sync', 'paiements', 'Facturation : exporter la file « À facturer », reporter les numéros de facture', false),
  p('commission.read', 'commissions', 'Voir commissions / rémunérations (les siennes selon la portée)', true),
  p('commission.manage', 'commissions', 'Valider / payer commissions et rémunérations, saisir un ajustement', true),
  p('commission.export', 'commissions', 'Exporter les relevés (CSV / PDF)', true),
  p('sav.read', 'sav', 'Voir les SAV', true),
  p('sav.create', 'sav', 'Créer un SAV', true),
  p('sav.validate', 'sav', 'Valider / attribuer / clôturer un SAV', true),
  p('stock.level.read', 'stock', 'Voir la disponibilité (Disponible / faible / indisponible)', true),
  p('stock.read', 'stock', 'Voir les quantités de stock', true),
  p('stock.manage', 'stock', 'Ajuster le stock', true),
  p('contractor_document.read', 'equipe', 'Consulter les documents des sous-traitants', true),
  p('contractor_document.manage', 'equipe', 'Contrôler les documents des sous-traitants (valider / refuser, types de documents)', false),
  p('announcement.publish', 'ressources', 'Publier annonces et ressources (territoire : annonces ciblées sur ses territoires)', true),
  p('review.request', 'avis', 'Envoyer des demandes d’avis clients', true),
  p('stats.read', 'pilotage', 'Voir tableau de bord et statistiques (qualité)', true),
] as const satisfies readonly PermissionDef[];

export type PermissionCode = (typeof PERMISSIONS)[number]['code'];

export const PERMISSION_CODES = PERMISSIONS.map((x) => x.code) as PermissionCode[];

export function isPermissionCode(code: string): code is PermissionCode {
  return (PERMISSION_CODES as string[]).includes(code);
}

export function permissionDef(code: PermissionCode): PermissionDef {
  const def = PERMISSIONS.find((x) => x.code === code);
  if (!def) throw new Error(`Unknown permission ${code}`);
  return def;
}

/** Effective permissions of a user. */
export type EffectivePermissions = Partial<Record<PermissionCode, Scope>>;

export function mergeGrants(grants: { permission: string; scope: Scope }[]): EffectivePermissions {
  const out: EffectivePermissions = {};
  for (const g of grants) {
    if (!isPermissionCode(g.permission)) continue;
    out[g.permission] = maxScope(out[g.permission], g.scope);
  }
  return out;
}

/**
 * Per-user adjustments on top of the roles (cahier des charges : permissions superviseur
 * « ajustables individuellement », « droits retirables individuellement »).
 *  - `grant`  : adds the permission (or widens to `scope`)
 *  - `revoke` : removes the permission whatever the roles say
 */
export interface PermissionOverride {
  permission: string;
  mode: 'grant' | 'revoke';
  scope: Scope | null;
}

export function effectivePermissions(
  roleGrants: { permission: string; scope: Scope }[],
  overrides: PermissionOverride[],
): EffectivePermissions {
  const out = mergeGrants(roleGrants);
  for (const o of overrides) {
    if (!isPermissionCode(o.permission)) continue;
    if (o.mode === 'grant' && o.scope) out[o.permission] = maxScope(out[o.permission], o.scope);
  }
  for (const o of overrides) {
    if (isPermissionCode(o.permission) && o.mode === 'revoke') delete out[o.permission];
  }
  return out;
}

/**
 * Default role presets seeded for a new tenant. They are regular, editable roles
 * (flag `is_system` only prevents deletion of `admin`).
 */
export interface RolePreset {
  code: string;
  name: string;
  description: string;
  isSystem: boolean;
  grants: Partial<Record<PermissionCode, Scope>>;
}

const all = (codes: PermissionCode[], scope: Scope = 'all') =>
  Object.fromEntries(codes.map((c) => [c, scope])) as Partial<Record<PermissionCode, Scope>>;

/** Categories counted by subscription-plan quotas (cahier des charges §19). */
export type QuotaCategory = 'supervisor' | 'commercial' | 'technician';

export const ROLE_PRESETS: (RolePreset & { quotaCategory: QuotaCategory | null })[] = [
  {
    code: 'admin',
    name: 'Administrateur',
    description: 'Tout, tous territoires : paramètres, comptes, permissions, contrôle et validation.',
    isSystem: true,
    quotaCategory: null,
    grants: all(PERMISSION_CODES),
  },
  {
    code: 'superviseur',
    name: 'Superviseur',
    description:
      'Contrôle / renvoi / validation et planning selon les droits accordés par l’administrateur ; droits retirables individuellement.',
    isSystem: false,
    quotaCategory: 'supervisor',
    grants: {
      'user.read': 'all',
      'referential.read': 'all',
      'catalog.read': 'all',
      ...all(
        [
          'audit.read',
          'client.read',
          'client.create',
          'client.update',
          'dossier.read',
          'dossier.create',
          'dossier.update',
          'dossier.submit',
          'dossier.control',
          'dossier.schedule',
          'dossier.cancel',
          'dossier.assign',
          'dossier.amounts.read',
          'comment.private.read',
          'comment.private.write',
          'document.read',
          'document.upload',
          'planning.read',
          'planning.manage',
          'intervention.read',
          'payment.read',
          'payment.cash.confirm',
          'commission.read',
          'commission.export',
          'contractor_document.read',
          'sav.read',
          'sav.create',
          'sav.validate',
          'stock.level.read',
          'stock.read',
          'announcement.publish',
          'stats.read',
          'prospect.read',
          'prospect.manage',
          'review.request',
        ],
        'territory',
      ),
    },
  },
  {
    code: 'commercial',
    name: 'Commercial',
    description:
      'Crée et suit ses dossiers et ses clients ; aucun accès aux données des autres commerciaux (seulement des volumes anonymisés).',
    isSystem: false,
    quotaCategory: 'commercial',
    grants: {
      'referential.read': 'all',
      'catalog.read': 'all',
      'client.create': 'territory',
      'dossier.create': 'territory',
      ...all(
        [
          'client.read',
          'client.update',
          'dossier.read',
          'dossier.update',
          'dossier.submit',
          'dossier.delete',
          'dossier.amounts.read',
          'intervention.read',
          'document.read',
          'document.upload',
          'signature.collect',
          'portal.link.manage',
          'planning.read',
          'payment.read',
          'payment.collect',
          'commission.read',
          'commission.export',
          'sav.read',
          'sav.create',
          'stock.level.read',
          'prospect.read',
        ],
        'own',
      ),
    },
  },
  {
    code: 'technicien',
    name: 'Technicien',
    description:
      'Ses interventions et les informations nécessaires à la pose (adresse, photos, commentaires) ; aucun montant du client ni accès commercial, sauf cumul du rôle Commercial ou case « paiements » cochée.',
    isSystem: false,
    quotaCategory: 'technician',
    grants: {
      'referential.read': 'all',
      ...all(
        [
          'client.read',
          'dossier.read',
          'document.read',
          'document.upload',
          'planning.read',
          'intervention.read',
          'intervention.execute',
          // paiements : NON par défaut ; l'administrateur coche « voir et encaisser les paiements » par technicien
          'commission.read',
          'commission.export',
          'sav.read',
        ],
        'own',
      ),
    },
  },
];
